import { useEffect, useState } from "react";
import useAuth from "@/hooks/useAuth";
import useLang from "@/hooks/useLang";
import useStore from "@/hooks/useStore";
import useTranslation from "@/hooks/useTranslation";
import { buildBody, buildUrl } from "./helpers";
import type { FetchOptions, FetchResult } from "./types";

export default function useFetch<T = unknown>(path: string, baseOptions: FetchOptions = {}, init: boolean = true): FetchResult<T> {
    if (!path) throw new Error("Path is required for useFetch");

    const token = useAuth((state) => state.token);
    const updateStoreAccess = useAuth((state) => state.updateStoreAccess);
    const dropDeadSession = useAuth((state) => state.dropDeadSession);
    const storeId = useStore((state) => state.storeId);
    const lang = useLang((state) => state.lang);
    const t = useTranslation();

    const [pendingOptions, setPendingOptions] = useState<FetchOptions | null>(null);
    const [work, setWork] = useState(init ? 1 : 0);
    const [data, setData] = useState<T | null>(null);
    const [loading, setLoading] = useState(init);
    const [error, setError] = useState<string | null>(null);
    const [status, setStatus] = useState<number | null>(null);

    useEffect(() => {
        const controller = new AbortController();
        const options = { ...baseOptions, ...pendingOptions };

        async function fetching() {
            setLoading(true);
            setError(null);

            const url = buildUrl(path, options);
            const headers: Record<string, string> = { "x-language": lang ?? "ar" };
            if (token) headers["Authorization"] = `Bearer ${token}`;
            if (storeId) headers["x-store-id"] = storeId;

            const requestInit: RequestInit = { method: options.method ?? "GET", headers, signal: options.signal ?? controller.signal };

            if (options.data) {
                const { body, contentType } = buildBody(options.data);
                requestInit.body = body;
                if (contentType) headers["Content-Type"] = contentType;
            }

            try {
                const response = await fetch(url, requestInit);
                setStatus(response.status);

                /**
                 * Not everything that answers this URL is the API. nginx rejects an oversized upload
                 * with its own HTML 413 page and a dead upstream with an HTML 502, and parsing those
                 * before checking `ok` threw into the catch below — so a request the server had
                 * already explained was reported to the user as a network failure it was not.
                 */
                const json = await response.json().catch(() => null);

                /**
                 * The API answers 401 for one thing only: the token this request carried is no longer
                 * a session — expired, revoked, or belonging to a client that has since been deleted.
                 * Keeping it on the device leaves someone staring at a dashboard where every request
                 * fails and nothing explains why, so the identity is dropped here and the app sends
                 * them to sign in again.
                 *
                 * Guarded on having actually sent a token: logging in and verifying an OTP are public
                 * calls, and a 401 from those is a wrong code, not a dead session.
                 */
                if (response.status === 401 && token) {
                    dropDeadSession();
                } else if (response.ok && json) {
                    setData(json);
                    if (json.permissionsUpdated) updateStoreAccess(json.permissionsUpdated.store, json.permissionsUpdated.storeAccess);
                } else if (json?.message) {
                    setError(json.message);
                } else {
                    setError(response.status === 413 ? t.errors.tooLarge : t.errors.unknown);
                }
            } catch (err) {
                // An abort means this request was superseded or the component unmounted, so its
                // result belongs to nothing on screen. Returning before `setLoading(false)` matters:
                // clearing it here would end the loading state while the replacement request is
                // still in flight, and the screen would paint its empty state over live data.
                if ((err as Error)?.name === "AbortError") return;
                setError(t.errors.network);
            }

            setLoading(false);
        }

        if (work > 0) fetching();
        return () => controller.abort();
    }, [work]);

    const invoke = (overrideOptions?: FetchOptions) => {
        if (overrideOptions) setPendingOptions(overrideOptions);
        setWork((prev) => prev + 1);
    };

    return { data, loading, error, status, invoke };
}
